import { useState, useEffect } from "react";
import Papa from "papaparse";
import JSZip from "jszip";
import Warning from "src/components/Warning";

interface CDRMappingProps {
  file: File;
  onMappingComplete: (
    mappings: Record<string, string>,
    hasHeader: boolean,
  ) => void;
  onCancel: () => void;
}

const CDR_FIELDS = [
  { id: "PhoneNumber", label: "Phone Number", required: true },
  { id: "ANI", label: "ANI (Caller ID)", required: false },
  { id: "StartTime", label: "Start Time", required: true },
  { id: "EndTime", label: "End Time", required: false },
  { id: "CallDisposition", label: "Call Disposition", required: false },
  { id: "SIPCode", label: "SIP Code", required: false },
  { id: "CallLength", label: "Call Length", required: false },
];

// Alias dictionary: common CDR column name synonyms -> our field IDs
const ALIASES: Record<string, string> = {
  destination: "PhoneNumber",
  dialed: "PhoneNumber",
  called: "PhoneNumber",
  phone: "PhoneNumber",
  number: "PhoneNumber",
  caller: "ANI",
  callerid: "ANI",
  clid: "ANI",
  ani: "ANI",
  begin: "StartTime",
  start: "StartTime",
  finish: "EndTime",
  end: "EndTime",
  duration: "CallLength",
  length: "CallLength",
  outcome: "CallDisposition",
  result: "CallDisposition",
  disposition: "CallDisposition",
  sip: "SIPCode",
  response: "SIPCode",
};

// Tokenize a string by splitting on spaces, underscores, hyphens, and camelCase/acronym boundaries
function tokenize(s: string): string[] {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase: "startTime" → "start Time"
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") // acronyms: "SIPCode" → "SIP Code"
    .split(/[\s_\-]+/)
    .map((t) => t.toLowerCase())
    .filter(Boolean);
}

// Score how well a file header matches a CDR field
function scoreMatch(headerTokens: string[], fieldId: string): number {
  const fieldTokens = tokenize(fieldId);

  // Token overlap ratio
  const overlap = headerTokens.filter((t) => fieldTokens.includes(t)).length;
  const overlapRatio =
    overlap / Math.max(headerTokens.length, fieldTokens.length);

  // Alias bonus: if any header token maps to this field via aliases
  const aliasBonus = headerTokens.some((t) => ALIASES[t] === fieldId) ? 0.5 : 0;

  return overlapRatio + aliasBonus;
}

// Auto-match file columns to CDR fields
function autoMatch(headers: string[]): Record<string, string> {
  const mappings: Record<string, string> = {};
  const usedFields = new Set<string>();

  // Score all (header, field) pairs
  const scores: { colIdx: number; fieldId: string; score: number }[] = [];
  headers.forEach((header, colIdx) => {
    const tokens = tokenize(header);
    CDR_FIELDS.forEach((field) => {
      const score = scoreMatch(tokens, field.id);
      if (score >= 0.5) {
        scores.push({ colIdx, fieldId: field.id, score });
      }
    });
  });

  // Sort descending by score, then greedy assign
  scores.sort((a, b) => b.score - a.score);
  const usedCols = new Set<number>();
  for (const { colIdx, fieldId } of scores) {
    if (usedCols.has(colIdx) || usedFields.has(fieldId)) continue;
    mappings[String(colIdx)] = fieldId;
    usedCols.add(colIdx);
    usedFields.add(fieldId);
  }

  return mappings;
}

type ParseResult = { headers: string[]; rows: string[][] };

// Only the head of the file is read for the preview. PapaParse reads an entire File
// into memory when handed one (chunked reading requires a step/chunk callback), which
// on a multi-hundred-MB CDR file freezes the tab or exceeds the max JS string length
// and silently yields no rows at all.
const PREVIEW_BYTES = 1024 * 1024;

const SHEET_HEAD_BYTES = 4 * 1024 * 1024;

const SMALL_PART_BYTES = 8 * 1024 * 1024;

// A CSV can be head-sliced, but a spreadsheet has to be read whole to preview it. Cap that read so
// a pathologically large workbook can't exhaust browser memory. The server still processes the full
// upload, and large datasets can always be provided as CSV. Tune as needed.
const MAX_SPREADSHEET_PREVIEW_BYTES = 100 * 1024 * 1024; // 100 MB

function assertSpreadsheetFitsPreview(bytes: number): void {
  if (bytes > MAX_SPREADSHEET_PREVIEW_BYTES) {
    throw new Error(
      "This spreadsheet is too large to preview in the browser. Please upload it as a CSV.",
    );
  }
}

function inflatedSize(entry: JSZip.JSZipObject): number | undefined {
  return (entry as unknown as { _data?: { uncompressedSize?: number } })._data
    ?.uncompressedSize;
}

// Rows to parse: the header row plus 5 sample rows, or 5 sample rows when there's no header
function previewRowCount(hasHeader: boolean): number {
  return hasHeader ? 6 : 5;
}

// Drop the trailing partial line left behind when a read stops mid-record
function trimPartialLine(text: string): string {
  const lastNewline = text.lastIndexOf("\n");
  return lastNewline === -1 ? text : text.slice(0, lastNewline + 1);
}

// Read the first PREVIEW_BYTES of a file as text
async function readHead(file: File): Promise<string> {
  const text = await file.slice(0, PREVIEW_BYTES).text();
  return file.size <= PREVIEW_BYTES ? text : trimPartialLine(text);
}

function streamEntryHead(
  entry: JSZip.JSZipObject,
  maxBytes: number,
  stopAfter?: { marker: string; count: number },
): Promise<{ text: string; truncated: boolean }> {
  // internalStream is part of JSZip's runtime API but is absent from its type definitions
  const stream = (
    entry as unknown as {
      internalStream(type: "string"): JSZip.JSZipStreamHelper<string>;
    }
  ).internalStream("string");

  return new Promise((resolve, reject) => {
    let text = "";
    let seen = 0;
    let searchFrom = 0;
    let settled = false;

    // Resolve/reject once, and pause the stream so JSZip stops decompressing the rest of the entry.
    const settle = (action: () => void) => {
      if (settled) return;
      settled = true;
      stream.pause();
      action();
    };

    stream
      .on("data", (chunk) => {
        if (settled) return;
        text += chunk;

        if (stopAfter) {
          for (;;) {
            const at = text.indexOf(stopAfter.marker, searchFrom);
            if (at === -1) break;
            seen++;
            searchFrom = at + stopAfter.marker.length;
          }
          if (seen >= stopAfter.count) {
            settle(() => resolve({ text, truncated: true }));
            return;
          }
        }

        if (text.length >= maxBytes) {
          settle(() => resolve({ text, truncated: true }));
        }
      })
      .on("error", (err) => settle(() => reject(err)))
      .on("end", () => settle(() => resolve({ text, truncated: false })))
      .resume();
  });
}

// A ZIP entry can't be sliced the way a File can, so decompress it as a stream and stop
// once there's enough text for the preview. Inflating the whole entry hits the same wall
// as reading a whole File — a 100 MB archive can hold a CDR file no string can represent.
async function readZipEntryHead(entry: JSZip.JSZipObject): Promise<string> {
  const { text, truncated } = await streamEntryHead(entry, PREVIEW_BYTES);
  return truncated ? trimPartialLine(text) : text;
}

// Convert raw preview rows into { headers, rows } based on whether the file has a header row
function buildPreview(allRows: string[][], hasHeader: boolean): ParseResult {
  if (allRows.length === 0) return { headers: [], rows: [] };

  if (hasHeader) {
    const headers = allRows[0].map(String);
    return { headers, rows: allRows.slice(1, 6) };
  }

  // No header — generate synthetic column names, preview first 5 rows
  const colCount = Math.max(...allRows.map((r) => r.length));
  const headers = Array.from({ length: colCount }, (_, i) => `Column ${i + 1}`);
  return { headers, rows: allRows.slice(0, 5) };
}

const ROW_END = "</row>";
const STRING_END = "</si>";
const SHEET_END = "</sheetData></worksheet>";
const SHARED_STRINGS_PATH = "xl/sharedStrings.xml";
const WORKSHEET_PATH = /^xl\/worksheets\/[^/]+\.xml$/;
const SHARED_STRING_CELL = /<c[^>]*t="s"[^>]*>\s*<v>(\d+)<\/v>/g;
const EMPTY_SHARED_STRINGS =
  '<?xml version="1.0"?><sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="0" uniqueCount="0"/>';
const EMPTY_WORKSHEET =
  '<?xml version="1.0"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData/></worksheet>';

async function firstSheet(zip: JSZip): Promise<JSZip.JSZipObject | null> {
  const workbook = zip.file("xl/workbook.xml");
  const rels = zip.file("xl/_rels/workbook.xml.rels");

  if (workbook && rels) {
    const id = (await workbook.async("string")).match(
      /<sheets>.*?r:id="([^"]+)"/s,
    )?.[1];
    if (id) {
      const target = (await rels.async("string")).match(
        new RegExp(`Id="${id}"[^>]*Target="([^"]+)"`),
      )?.[1];
      const entry =
        target && zip.file(`xl/${target.replace(/^\/?(xl\/)?/, "")}`);
      if (entry) return entry;
    }
  }

  const sheets = Object.values(zip.files).filter(
    (entry) => !entry.dir && WORKSHEET_PATH.test(entry.name),
  );
  return sheets.length === 1 ? sheets[0] : null;
}

async function xlsxPreviewWorkbook(
  buffer: ArrayBuffer,
  hasHeader: boolean,
): Promise<Uint8Array | null> {
  const zip = await JSZip.loadAsync(buffer);
  const sheetEntry = await firstSheet(zip);
  if (!sheetEntry) return null;

  const { text: head, truncated } = await streamEntryHead(
    sheetEntry,
    SHEET_HEAD_BYTES,
    { marker: ROW_END, count: previewRowCount(hasHeader) },
  );

  const lastRow = head.lastIndexOf(ROW_END);
  let sheetXml: string;
  if (lastRow !== -1) {
    sheetXml = head.slice(0, lastRow + ROW_END.length) + SHEET_END;
  } else if (!truncated) {
    sheetXml = head;
  } else {
    return null;
  }

  let sharedStringsXml = EMPTY_SHARED_STRINGS;
  const sharedEntry = zip.file(SHARED_STRINGS_PATH);
  if (sharedEntry) {
    let highestIndex = -1;
    for (const cell of sheetXml.matchAll(SHARED_STRING_CELL)) {
      highestIndex = Math.max(highestIndex, Number(cell[1]));
    }
    if (highestIndex >= 0) {
      const { text } = await streamEntryHead(sharedEntry, SHEET_HEAD_BYTES, {
        marker: STRING_END,
        count: highestIndex + 1,
      });
      const lastString = text.lastIndexOf(STRING_END);
      sharedStringsXml =
        lastString === -1
          ? text
          : text.slice(0, lastString + STRING_END.length) + "</sst>";
    }
  }

  const preview = new JSZip();
  for (const entry of Object.values(zip.files)) {
    if (entry.dir) continue;

    if (entry.name === sheetEntry.name) {
      preview.file(entry.name, sheetXml);
    } else if (entry.name === SHARED_STRINGS_PATH) {
      preview.file(entry.name, sharedStringsXml);
    } else if (WORKSHEET_PATH.test(entry.name)) {
      preview.file(entry.name, EMPTY_WORKSHEET);
    } else if ((inflatedSize(entry) ?? 0) <= SMALL_PART_BYTES) {
      preview.file(entry.name, await entry.async("uint8array"));
    }
  }

  return preview.generateAsync({ type: "uint8array" });
}

function parseXlsx(
  buffer: ArrayBuffer | Uint8Array,
  hasHeader: boolean,
): Promise<ParseResult> {
  // Dynamic import — xlsx chunk only loads when an XLSX file is encountered
  return import("xlsx").then((XLSX) => {
    const workbook = XLSX.read(buffer, {
      type: "array",
      sheetRows: previewRowCount(hasHeader),
    });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows: string[][] = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      blankrows: false,
    });
    return buildPreview(rows, hasHeader);
  });
}

function parseCsv(input: string, hasHeader: boolean): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    Papa.parse(input, {
      preview: previewRowCount(hasHeader),
      skipEmptyLines: true,
      complete: (results) =>
        resolve(buildPreview(results.data as string[][], hasHeader)),
      error: (err: Error) => reject(err),
    });
  });
}

function getExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

async function parseSpreadsheet(
  buffer: ArrayBuffer,
  ext: string,
  hasHeader: boolean,
): Promise<ParseResult> {
  if (ext === "xlsx") {
    const preview = await xlsxPreviewWorkbook(buffer, hasHeader).catch(
      () => null,
    );
    if (preview) {
      const parsed = await parseXlsx(preview, hasHeader).catch(() => null);
      if (parsed) return parsed;
    }
  }
  return parseXlsx(buffer, hasHeader);
}

export async function parseFile(
  file: File,
  hasHeader: boolean,
): Promise<ParseResult> {
  const ext = getExtension(file.name);

  // ZIP: extract the single CDR file inside and parse it
  if (ext === "zip") {
    const zip = await JSZip.loadAsync(file);

    // Filter out macOS metadata (__MACOSX/, .DS_Store, ._* resource forks)
    const SUPPORTED_EXTENSIONS = ["csv", "xlsx", "xls"];
    const entries = Object.values(zip.files).filter((e) => {
      if (e.dir) return false;
      const name = e.name;
      if (name.startsWith("__MACOSX/") || name.startsWith("._")) return false;
      if (name === ".DS_Store") return false;
      return true;
    });

    if (entries.length !== 1) {
      throw new Error(
        `ZIP must contain exactly 1 CDR file (CSV or XLSX), but found ${entries.length} files.`,
      );
    }

    const inner = entries[0];
    const innerExt = getExtension(inner.name);

    if (!SUPPORTED_EXTENSIONS.includes(innerExt)) {
      throw new Error(
        `Unsupported file type inside ZIP: .${innerExt}. Expected a CSV or XLSX file.`,
      );
    }

    if (innerExt === "xlsx" || innerExt === "xls") {
      assertSpreadsheetFitsPreview(inflatedSize(inner) ?? file.size);
      return parseSpreadsheet(
        await inner.async("arraybuffer"),
        innerExt,
        hasHeader,
      );
    }

    return parseCsv(await readZipEntryHead(inner), hasHeader);
  }

  if (ext === "xlsx" || ext === "xls") {
    assertSpreadsheetFitsPreview(file.size);
    return parseSpreadsheet(await file.arrayBuffer(), ext, hasHeader);
  }

  return parseCsv(await readHead(file), hasHeader);
}

const CDRMapping: React.FC<CDRMappingProps> = ({
  file,
  onMappingComplete,
  onCancel,
}) => {
  const [hasHeader, setHasHeader] = useState(true);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [parseError, setParseError] = useState("");
  const [isParsing, setIsParsing] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsParsing(true);
    setParseError("");

    parseFile(file, hasHeader)
      .then(({ headers, rows }) => {
        if (cancelled) return;
        setHeaders(headers);
        setPreviewRows(rows);
        setMappings(hasHeader ? autoMatch(headers) : {});
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Failed to parse file. Please check the file format.";
        setParseError(message);
      })
      .finally(() => {
        if (!cancelled) setIsParsing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [file, hasHeader]);

  const handleMappingChange = (colIdx: number, fieldId: string) => {
    setMappings((prev) => {
      const next = { ...prev };
      // Remove any existing mapping to this field (prevent duplicates)
      if (fieldId) {
        for (const [key, val] of Object.entries(next)) {
          if (val === fieldId) delete next[key];
        }
        next[String(colIdx)] = fieldId;
      } else {
        delete next[String(colIdx)];
      }
      return next;
    });
  };

  const mappedFields = new Set(Object.values(mappings));
  const missingRequired = CDR_FIELDS.filter(
    (f) => f.required && !mappedFields.has(f.id),
  );

  const handleSubmit = () => {
    if (missingRequired.length > 0) return;
    const namedMappings: Record<string, string> = {};
    for (const [colIdx, fieldId] of Object.entries(mappings)) {
      // When no header, send column index so backend can locate by position
      namedMappings[fieldId] = hasHeader
        ? headers[Number(colIdx)]
        : String(Number(colIdx));
    }
    onMappingComplete(namedMappings, hasHeader);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-diagram-3 me-2"></i>
          Map CDR Columns
        </h5>
        <small className="text-muted">{file.name}</small>
      </div>
      <div className="card-body">
        {parseError && <Warning message={parseError} />}

        {/* Header toggle */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="hasHeaderToggle"
            checked={hasHeader}
            onChange={(e) => setHasHeader(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="hasHeaderToggle">
            File has a header row
          </label>
        </div>

        {isParsing ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Parsing file preview...</p>
          </div>
        ) : (
          <>
            {/* Mapping table */}
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead>
                  {/* Dropdown row */}
                  <tr className="table-light">
                    {headers.map((_header, colIdx) => (
                      <th key={colIdx} style={{ minWidth: 160 }}>
                        <select
                          className={`form-select form-select-sm ${
                            mappings[String(colIdx)] ? "border-success" : ""
                          }`}
                          value={mappings[String(colIdx)] || ""}
                          onChange={(e) =>
                            handleMappingChange(colIdx, e.target.value)
                          }
                        >
                          <option value="">-- Skip --</option>
                          {CDR_FIELDS.map((field) => (
                            <option
                              key={field.id}
                              value={field.id}
                              disabled={
                                mappedFields.has(field.id) &&
                                mappings[String(colIdx)] !== field.id
                              }
                            >
                              {field.label}
                              {field.required ? " *" : ""}
                            </option>
                          ))}
                        </select>
                      </th>
                    ))}
                  </tr>
                  {/* Header row */}
                  <tr>
                    {headers.map((header, colIdx) => (
                      <th key={colIdx} className="text-muted fw-normal">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {headers.map((_, colIdx) => (
                        <td
                          key={colIdx}
                          className="text-truncate"
                          style={{ maxWidth: 200 }}
                        >
                          {row[colIdx] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Validation */}
            {missingRequired.length > 0 && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Required fields not mapped:{" "}
                <strong>
                  {missingRequired.map((f) => f.label).join(", ")}
                </strong>
              </div>
            )}
          </>
        )}
      </div>
      <div className="card-footer d-flex justify-content-end gap-2">
        <button className="btn btn-outline-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={isParsing || missingRequired.length > 0}
        >
          <i className="bi bi-check-lg me-1"></i>
          Confirm Mapping & Upload
        </button>
      </div>
    </div>
  );
};

export default CDRMapping;
